/**
 * Localized Pricing — sales display layer.
 *
 * Why this exists:
 *   The landing showed €59 to everyone. Non-EU visitors had to mental-math
 *   the conversion → friction → bounce. We now display in the visitor's
 *   currency (EUR/USD/GBP) for psychological clarity.
 *
 * Important scope boundary:
 *   This module governs **display** prices only — what the marketing page
 *   shows. The Stripe checkout itself still settles in EUR (Stripe handles
 *   the FX at card-side). Phase 2 will introduce per-currency Stripe Price
 *   IDs so checkout stays in the displayed currency end-to-end.
 *
 * Rates here are **sales rates**, not financial conversions: we pick
 * round, psychologically-anchored numbers ($65, £52) rather than tracking
 * the EUR→USD spot rate. Visitors compare to "is it worth 65 dollars",
 * not "is the EUR rate fair today".
 */

export type Currency =
  | 'EUR'
  | 'USD'
  | 'GBP'
  | 'CHF'
  | 'CAD'
  | 'AUD'
  | 'PLN'
  | 'CZK'
  | 'SEK'
  | 'NOK'
  | 'DKK'
  | 'TRY'
  | 'AED';

export const SUPPORTED_CURRENCIES: readonly Currency[] = [
  'EUR', 'USD', 'GBP', 'CHF', 'CAD', 'AUD', 'PLN', 'CZK', 'SEK', 'NOK', 'DKK', 'TRY', 'AED',
] as const;

export const DEFAULT_CURRENCY: Currency = 'EUR';

/**
 * Cookie name for the visitor's persisted currency override.
 *
 * Defined here (pure, no runtime deps) so both server.ts (next/headers)
 * and client.ts (document.cookie) can import it without dragging the
 * server module into the client bundle.
 */
export const CURRENCY_COOKIE = 'eb_currency';
export const CURRENCY_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

/** ISO-3166-1 alpha-2 countries that map to EUR (eurozone + pegged). */
const EUR_COUNTRIES = new Set([
  'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT',
  'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK',
  // Non-EU but EUR-pegged / heavy EUR commerce — treat as EUR for clarity.
  'AD', 'MC', 'SM', 'VA', 'XK',
]);

/** Countries that get USD by default. */
const USD_COUNTRIES = new Set([
  'US', 'NZ', 'JP', 'SG', 'HK', 'KR', 'TW',
  'BR', 'AR', 'CL', 'CO', 'PE',
  'PH', 'TH', 'VN', 'ID', 'MY',
  'IN', 'IL', 'SA',
]);

const GBP_COUNTRIES = new Set(['GB', 'IM', 'JE', 'GG', 'GI']);
const CHF_COUNTRIES = new Set(['CH', 'LI']);
const CAD_COUNTRIES = new Set(['CA']);
const AUD_COUNTRIES = new Set(['AU']);
// Mexico has its own Peso (MXN) but we don't offer that; keep MX → USD.
const PLN_COUNTRIES = new Set(['PL']);
const CZK_COUNTRIES = new Set(['CZ']);
const SEK_COUNTRIES = new Set(['SE']);
const NOK_COUNTRIES = new Set(['NO', 'SJ']);
const DKK_COUNTRIES = new Set(['DK', 'FO', 'GL']);
const TRY_COUNTRIES = new Set(['TR']);
const AED_COUNTRIES = new Set(['AE']);

/**
 * Country code → currency. Geo-detect priority order: EUR → GBP →
 * smaller national currencies → USD as catch-all default for
 * unmapped LatAm/APAC visitors.
 *
 * Rationale for USD default (instead of EUR):
 *   If we don't know where someone is, they're more likely outside the
 *   eurozone than inside it (because we already enumerate eurozone above).
 *   For unknowns, USD beats EUR for clarity.
 */
export function countryToCurrency(countryCode: string | null | undefined): Currency {
  if (!countryCode) return DEFAULT_CURRENCY;
  const cc = countryCode.toUpperCase();
  if (EUR_COUNTRIES.has(cc)) return 'EUR';
  if (GBP_COUNTRIES.has(cc)) return 'GBP';
  if (CHF_COUNTRIES.has(cc)) return 'CHF';
  if (CAD_COUNTRIES.has(cc)) return 'CAD';
  if (AUD_COUNTRIES.has(cc)) return 'AUD';
  if (PLN_COUNTRIES.has(cc)) return 'PLN';
  if (CZK_COUNTRIES.has(cc)) return 'CZK';
  if (SEK_COUNTRIES.has(cc)) return 'SEK';
  if (NOK_COUNTRIES.has(cc)) return 'NOK';
  if (DKK_COUNTRIES.has(cc)) return 'DKK';
  if (TRY_COUNTRIES.has(cc)) return 'TRY';
  if (AED_COUNTRIES.has(cc)) return 'AED';
  if (USD_COUNTRIES.has(cc)) return 'USD';
  return DEFAULT_CURRENCY;
}

export function isSupportedCurrency(v: unknown): v is Currency {
  return typeof v === 'string' && (SUPPORTED_CURRENCIES as readonly string[]).includes(v);
}

/**
 * The price table — sales prices, not FX-derived. Round numbers chosen
 * for psychological anchoring. Yearly = 10× monthly (still "2 months
 * free" framing — 12 months at 10× monthly).
 *
 * Keep this table in lock-step with the Stripe Price IDs in
 * STRIPE_PRICES (Phase 2). Until then, monthly = EUR-equivalent and
 * Stripe will FX at checkout.
 */
interface PriceTable {
  /** Whole monthly price for casual display. */
  monthly: number;
  /** Effective monthly price under the yearly plan. */
  yearlyPerMonth: number;
  /** What 12 months of monthly would cost (the strikethrough number). */
  monthlyTotal: number;
  /** Yearly plan total (the "you pay this once" number). */
  yearlyTotal: number;
  /** Savings = monthlyTotal − yearlyTotal. Pre-computed so UI never math-errors. */
  savings: number;
  /** Decimal-rendered string for the per-month-yearly slot (e.g., "49,17" or "54.08"). */
  yearlyPerMonthDisplay: string;
}

// Sales prices, not FX-derived. Round numbers chosen for psychological
// anchoring (€59, $65, £52). Yearly = 10× monthly = "2 months free"
// framing across all currencies. Monthly numbers cluster around the
// €59 EUR base × current FX × ~10% safety margin. Keep in lock-step
// with Stripe Price.currency_options on prices
// `price_1TZot4IAPcqi3Cvz3qUqpEmV` (monthly Professional) and
// `price_1TZotQIAPcqi3CvzGZk9gJWy` (yearly Professional).
const PRICE_TABLES: Record<Currency, PriceTable> = {
  EUR: { monthly: 59,  yearlyPerMonth: 49.17, monthlyTotal: 708,   yearlyTotal: 590,   savings: 118,  yearlyPerMonthDisplay: '49,17' },
  USD: { monthly: 65,  yearlyPerMonth: 54.08, monthlyTotal: 780,   yearlyTotal: 649,   savings: 131,  yearlyPerMonthDisplay: '54.08' },
  GBP: { monthly: 52,  yearlyPerMonth: 43.25, monthlyTotal: 624,   yearlyTotal: 519,   savings: 105,  yearlyPerMonthDisplay: '43.25' },
  CHF: { monthly: 65,  yearlyPerMonth: 54.08, monthlyTotal: 780,   yearlyTotal: 649,   savings: 131,  yearlyPerMonthDisplay: "54.08" },
  CAD: { monthly: 89,  yearlyPerMonth: 74.08, monthlyTotal: 1068,  yearlyTotal: 889,   savings: 179,  yearlyPerMonthDisplay: '74.08' },
  AUD: { monthly: 99,  yearlyPerMonth: 82.42, monthlyTotal: 1188,  yearlyTotal: 989,   savings: 199,  yearlyPerMonthDisplay: '82.42' },
  PLN: { monthly: 249, yearlyPerMonth: 207.50,monthlyTotal: 2988,  yearlyTotal: 2490,  savings: 498,  yearlyPerMonthDisplay: '207,50' },
  CZK: { monthly: 1399,yearlyPerMonth: 1165.83,monthlyTotal: 16788,yearlyTotal: 13990, savings: 2798, yearlyPerMonthDisplay: '1 165,83' },
  SEK: { monthly: 649, yearlyPerMonth: 540.83,monthlyTotal: 7788,  yearlyTotal: 6490,  savings: 1298, yearlyPerMonthDisplay: '540,83' },
  NOK: { monthly: 649, yearlyPerMonth: 540.83,monthlyTotal: 7788,  yearlyTotal: 6490,  savings: 1298, yearlyPerMonthDisplay: '540,83' },
  DKK: { monthly: 449, yearlyPerMonth: 374.17,monthlyTotal: 5388,  yearlyTotal: 4490,  savings: 898,  yearlyPerMonthDisplay: '374,17' },
  TRY: { monthly: 1999,yearlyPerMonth: 1665.83,monthlyTotal: 23988,yearlyTotal: 19990, savings: 3998, yearlyPerMonthDisplay: '1.665,83' },
  AED: { monthly: 249, yearlyPerMonth: 207.50,monthlyTotal: 2988,  yearlyTotal: 2490,  savings: 498,  yearlyPerMonthDisplay: '207.50' },
};

export interface CurrencySpec {
  code: Currency;
  /** Symbol shown in the big-price slot. */
  symbol: string;
  /** Per-currency price table (sales prices, see above). */
  prices: PriceTable;
  /**
   * Whether the locale prefixes the symbol ($65) or suffixes it (65 €).
   * EUR in German convention is suffix; USD and GBP prefix.
   */
  symbolPosition: 'prefix' | 'suffix';
}

const SPEC_BY_CURRENCY: Record<Currency, Omit<CurrencySpec, 'prices'>> = {
  EUR: { code: 'EUR', symbol: '€',   symbolPosition: 'suffix' },
  USD: { code: 'USD', symbol: '$',   symbolPosition: 'prefix' },
  GBP: { code: 'GBP', symbol: '£',   symbolPosition: 'prefix' },
  CHF: { code: 'CHF', symbol: 'CHF', symbolPosition: 'prefix' },
  CAD: { code: 'CAD', symbol: 'CA$', symbolPosition: 'prefix' },
  AUD: { code: 'AUD', symbol: 'A$',  symbolPosition: 'prefix' },
  PLN: { code: 'PLN', symbol: 'zł',  symbolPosition: 'suffix' },
  CZK: { code: 'CZK', symbol: 'Kč',  symbolPosition: 'suffix' },
  SEK: { code: 'SEK', symbol: 'kr',  symbolPosition: 'suffix' },
  NOK: { code: 'NOK', symbol: 'kr',  symbolPosition: 'suffix' },
  DKK: { code: 'DKK', symbol: 'kr',  symbolPosition: 'suffix' },
  TRY: { code: 'TRY', symbol: '₺',   symbolPosition: 'suffix' },
  AED: { code: 'AED', symbol: 'AED', symbolPosition: 'prefix' },
};

export function getCurrencySpec(currency: Currency): CurrencySpec {
  return { ...SPEC_BY_CURRENCY[currency], prices: PRICE_TABLES[currency] };
}

/**
 * Format a whole number for display in the given currency.
 *   formatPrice(118, 'EUR') → "118 €"
 *   formatPrice(131, 'USD') → "$131"
 *   formatPrice(105, 'GBP') → "£105"
 *
 * Decimal numbers (49.17, 54.08) are NOT routed through here — those are
 * already rendered to locale-correct strings in `yearlyPerMonthDisplay`
 * because Intl.NumberFormat with `style: 'currency'` would force the
 * symbol position and clash with our `symbolPosition` field.
 */
export function formatPrice(amount: number, currency: Currency): string {
  const spec = SPEC_BY_CURRENCY[currency];
  const whole = Math.round(amount).toString();
  return spec.symbolPosition === 'prefix'
    ? `${spec.symbol}${whole}`
    : `${whole} ${spec.symbol}`;
}

/**
 * Monthly Professional price, normalised across all 13 supported
 * currencies. Used by comparison landings + pricing-card to render
 * "59 €", "$65", "£52", etc. — psychologically-anchored round numbers
 * derived from EUR via PRICE_TABLES.
 *
 * @example
 *   getMonthlyPrice('EUR') → 59
 *   getMonthlyPrice('USD') → 65
 */
export function getMonthlyPrice(currency: Currency): number {
  return PRICE_TABLES[currency].monthly;
}

/**
 * Convenience helper for marketing copy: returns the per-currency
 * monthly price already formatted with symbol — saves call-sites from
 * looking up the value + formatPrice separately. Used by the four
 * `/x-alternative` comparison pages.
 *
 * @example
 *   getMonthlyPriceDisplay('EUR') → "59 €"
 *   getMonthlyPriceDisplay('USD') → "$65"
 */
export function getMonthlyPriceDisplay(currency: Currency): string {
  return formatPrice(getMonthlyPrice(currency), currency);
}

// ────────────────────────────────────────────────────────────────────
// Stripe Integration Helpers (Phase 2 — end-to-end localized checkout)
// ────────────────────────────────────────────────────────────────────

/**
 * Stripe expects lowercase ISO-4217 currency codes ('eur', not 'EUR').
 * This is the only place we do the case conversion — everywhere else in
 * the app we use the capitalized `Currency` type.
 */
export type StripeCurrencyCode =
  | 'eur' | 'usd' | 'gbp' | 'chf' | 'cad' | 'aud'
  | 'pln' | 'czk' | 'sek' | 'nok' | 'dkk' | 'try' | 'aed';

export function toStripeCurrency(c: Currency): StripeCurrencyCode {
  return c.toLowerCase() as StripeCurrencyCode;
}

/**
 * Convert a major-unit amount (€59.00) into Stripe's minor units (5900
 * cents). All currencies we support are 2-decimal — JPY/KRW would need
 * zero-decimal handling, but they're not in scope.
 */
export function toMinorUnits(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * Build the `currency_options` block for Stripe's Price.create / update.
 *
 * Why this shape:
 *   Stripe Prices support multi-currency: one Price ID can charge in
 *   multiple currencies, picked at checkout via `session.currency`. This
 *   beats creating 1 Price ID per (plan × cycle × currency) because:
 *     - Plan changes don't need a 6-way mapping table
 *     - Webhooks don't need to disambiguate which Price was used
 *     - The yearly/monthly toggle stays a single Price ID per cycle
 *
 * Usage:
 *   stripe.prices.create({
 *     product, recurring,
 *     currency: 'eur', unit_amount: 5900,
 *     currency_options: buildCurrencyOptions('monthly'),  // {usd, gbp}
 *   });
 */
type Cycle = 'monthly' | 'yearly';
type StripeCurrencyOption = {
  unit_amount: number;
  tax_behavior?: 'exclusive' | 'inclusive' | 'unspecified';
};

export function buildCurrencyOptions(
  cycle: Cycle,
  options: { taxBehavior?: 'exclusive' | 'inclusive' } = {},
): Record<Exclude<StripeCurrencyCode, 'eur'>, StripeCurrencyOption> {
  const { taxBehavior = 'exclusive' } = options;
  // EUR is the base currency on the Price; every other supported
  // currency goes into currency_options. Stripe Checkout picks the
  // matching unit_amount based on `session.currency` at create-time.
  const pick = (c: Currency) =>
    cycle === 'monthly'
      ? PRICE_TABLES[c].monthly
      : PRICE_TABLES[c].yearlyTotal;
  const make = (c: Currency): StripeCurrencyOption => ({
    unit_amount: toMinorUnits(pick(c)),
    tax_behavior: taxBehavior,
  });
  return {
    usd: make('USD'),
    gbp: make('GBP'),
    chf: make('CHF'),
    cad: make('CAD'),
    aud: make('AUD'),
    pln: make('PLN'),
    czk: make('CZK'),
    sek: make('SEK'),
    nok: make('NOK'),
    dkk: make('DKK'),
    try: make('TRY'),
    aed: make('AED'),
  };
}

/**
 * The base (EUR) amount and currency for a given cycle. Used by both
 * `Price.create` and webhook validation.
 */
/**
 * Map our 7 app locales to Stripe Checkout's `locale` enum. All seven
 * are natively supported by Stripe — this is just a 1:1 passthrough
 * with a safe fallback for anything unexpected.
 *
 * Why we need this: `locale: 'de'` was hardcoded in both checkout
 * sessions, so English/French/Spanish visitors got German Stripe UI.
 * That's a credibility leak right at the payment moment.
 */
/**
 * Permissive string return — Stripe's SDK locale-union sometimes lags
 * behind the live API (e.g. 'ar' is accepted at runtime but missing in
 * the typings of older SDK versions). Call-sites cast to the Stripe
 * type at the boundary so the typecheck stays clean across SDK bumps.
 */
type StripeCheckoutLocale = string;

/**
 * Pre-formatted price strings for the visitor's currency. Used by any
 * surface outside PricingV3 (FAQ, Hero, comparison pages) that mentions
 * the price — they get the formatted strings instead of recomputing.
 *
 * Returns whole-number prices (€59, $65, £52) for the monthly slot and
 * the yearly-total (€590, $649, £519). This is the i18n-template
 * contract: all locale strings reference these two slots via {monthly}
 * and {yearly}.
 */
export function getDisplayPrices(currency: Currency): {
  monthly: string;
  yearly: string;
} {
  const { prices } = getCurrencySpec(currency);
  return {
    monthly: formatPrice(prices.monthly, currency),
    yearly: formatPrice(prices.yearlyTotal, currency),
  };
}

export function toStripeLocale(appLocale: string | null | undefined): StripeCheckoutLocale {
  const supported: Record<string, StripeCheckoutLocale> = {
    de: 'de', en: 'en', fr: 'fr', es: 'es', ar: 'ar', ru: 'ru', tr: 'tr',
  };
  if (!appLocale) return 'auto';
  return supported[appLocale.toLowerCase()] ?? 'auto';
}

export function getBaseAmount(cycle: Cycle): {
  unitAmount: number;
  currency: 'eur';
} {
  return {
    unitAmount: toMinorUnits(
      cycle === 'monthly'
        ? PRICE_TABLES.EUR.monthly
        : PRICE_TABLES.EUR.yearlyTotal,
    ),
    currency: 'eur',
  };
}
